import { AfterViewInit, Directive, Host, Input, OnChanges, Optional, Renderer2, SimpleChanges } from '@angular/core';
import { DziViewerComponent } from '../../dzi-viewer.component';

type ScalebarPosition = 'CENTER' | 'TOP_LEFT' | 'TOP_RIGHT' | 'BOTTOM_LEFT' | 'BOTTOM_CENTER' | 'BOTTOM_RIGHT';

@Directive({
  selector: '[tsDziScalebar]',
  standalone: false,
})
export class DziScalebarDirective implements OnChanges, AfterViewInit {
  @Input() hspan: number;
  @Input() position: ScalebarPosition = 'CENTER';

  private _id = 'scalebar';
  private _referenceValues: number[] = [0.01, 0.02, 0.025, 0.05, 0.1, 0.2, 0.25, 0.5, 1, 2, 2.5, 5, 10]; // in millimeters (mm)

  constructor(@Host() @Optional() private dziViewer: DziViewerComponent, private renderer: Renderer2) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (!changes['hspan']) return;

    if (!changes['hspan'].currentValue) {
      this.removeScaleBar();
      return;
    }

    this.addScaleBar();
  }

  ngAfterViewInit() {
    if (!this.dziViewer) return;

    this.dziViewer['viewer'].addHandler('zoom', () => {
      this.updateScaleBar();
    });

    // hspan may arrive via ngOnChanges before the host creates the OSD viewer
    if (this.hspan) this.addScaleBar();
  }

  private addScaleBar(): void {
    const container = this.dziViewer?.['viewer']?.container;

    if (!container) return;

    let element = this.getScaleBarElement();

    if (!element) {
      element = this.renderer.createElement('div');
      this.renderer.setAttribute(element, 'id', this._id);

      this.renderer.setStyle(element, 'position', 'absolute');
      this.renderer.setStyle(element, 'height', '28px');
      this.renderer.setStyle(element, 'borderBottom', '4px solid #0099bb');
      this.renderer.setStyle(element, 'backgroundColor', 'rgba(255,255,255,0.7)');
      this.renderer.setStyle(element, 'zIndex', '3');
      this.renderer.setStyle(element, 'textAlign', 'center');
      this.renderer.setStyle(element, 'lineHeight', '28px');

      this.renderer.appendChild(container, element);
    }

    this.applyPositionStyles(element);
    this.updateScaleBar();
  }

  private removeScaleBar(): void {
    const element = this.getScaleBarElement();

    if (!element) return;

    this.renderer.removeChild(element.parentElement, element);
  }

  private getScaleBarElement(): HTMLElement | null {
    // Scoped to this viewer's container: document.getElementById is page-global
    return this.dziViewer?.['viewer']?.container.querySelector<HTMLElement>(`#${this._id}`) ?? null;
  }

  private applyPositionStyles(element: HTMLElement): void {
    this.renderer.removeStyle(element, 'top');
    this.renderer.removeStyle(element, 'bottom');
    this.renderer.removeStyle(element, 'left');
    this.renderer.removeStyle(element, 'right');
    this.renderer.removeStyle(element, 'transform');

    switch (this.position) {
      case 'TOP_LEFT':
        this.renderer.setStyle(element, 'top', '10px');
        this.renderer.setStyle(element, 'left', '10px');
        break;
      case 'TOP_RIGHT':
        this.renderer.setStyle(element, 'top', '10px');
        this.renderer.setStyle(element, 'right', '10px');
        break;
      case 'BOTTOM_LEFT':
        this.renderer.setStyle(element, 'bottom', '10px');
        this.renderer.setStyle(element, 'left', '10px');
        break;
      case 'BOTTOM_CENTER':
        this.renderer.setStyle(element, 'bottom', '10px');
        this.renderer.setStyle(element, 'left', '50%');
        this.renderer.setStyle(element, 'transform', 'translateX(-50%)');
        break;
      case 'BOTTOM_RIGHT':
        this.renderer.setStyle(element, 'bottom', '10px');
        this.renderer.setStyle(element, 'right', '10px');
        break;
      case 'CENTER':
      default:
        this.renderer.setStyle(element, 'bottom', '125px');
        this.renderer.setStyle(element, 'left', '50%');
        this.renderer.setStyle(element, 'transform', 'translate(-50%, -50%)');
        break;
    }
  }

  private updateScaleBar(): void {
    const scaleBarEl = this.getScaleBarElement();
    // The viewport only exists once OSD has opened a tile source
    const viewport = this.dziViewer?.['viewer']?.viewport;

    if (!scaleBarEl || !viewport || !this.hspan) return;

    const zoom = viewport.getZoom();
    const containerWidth = viewport.getContainerSize().x;

    const divWidthMm = this.hspan / (zoom * 5); // Max 1/5 of the viewer width
    const nearestReference = this._referenceValues.reduce((nearest, ref) =>
      Math.abs(divWidthMm - ref) < Math.abs(divWidthMm - nearest) ? ref : nearest
    );

    const scaleFactor = nearestReference / divWidthMm;
    const pixelWidth = (scaleFactor * containerWidth) / 5;

    const label = nearestReference >= 1 ? `${nearestReference} mm` : `${nearestReference * 1000} µm`;

    this.renderer.setStyle(scaleBarEl, 'width', `${pixelWidth}px`);
    this.renderer.setProperty(scaleBarEl, 'innerText', label);
  }
}
